import { Catalog, ChatIcon, LeadIcon, SettingIcon } from "@lhd/assets";
import { useConfirmingLeave } from "@lhd/app/index";
import CustomVerticalScrollBar from "@lhd/components/CustomVerticalScrollBar";
import {
  RELEASE_VERSION,
  ROUTES,
  SIDEBAR_COLORS,
  Status,
} from "@lhd/constants";
import {
  useGetAuth,
  useGetPermissions,
  useReAuthenticationOneTime,
} from "@lhd/hooks";
import {
  getCompanyId,
  getEffectiveIsDisplayInNewTab,
  IS_DISPLAY_IN_NEW_TAB_PARAM,
} from "@lhd/utility";
import { notification, Tooltip } from "antd";
import {
  cloneElement,
  Dispatch,
  Fragment,
  memo,
  SetStateAction,
  useEffect,
  useLayoutEffect,
  useMemo,
  useRef,
  useState,
} from "react";
import { isMobileOnly } from "react-device-detect";
import {
  FaChevronDown,
  FaChevronLeft,
  FaChevronRight,
  FaChevronUp,
} from "react-icons/fa";
import { useDispatch, useSelector } from "react-redux";
import { useLocation, useNavigate, useSearchParams } from "react-router-dom";
import { setBannerVisibility } from "../../components/CommonLayout/maintenanceBannerSlice";
import ButtonVerticalCollapse from "../ButtonVerticalCollapse";
import { getIsBannerVisible } from "../CommonLayout/sidebarSelector";
import { setIsMobileMenuOpen } from "../CommonLayout/sidebarSlice";
import CollapsedSidebar from "./CollapsedSidebar";
import { MENU_ITEM_IDS, TOOLTIP_MESSAGES } from "./constants";
import { useNavItem } from "./NavItems";
import { SharedTooltip } from "./SharedTooltip";
import {
  CustomerAgentTextWrapper,
  CustomerInquiryIconWrapper,
  CustomerInquiryTextWrapper,
  ListMenu,
  MenuItemContent,
  MenuItemLink,
  MenuItemTitle,
  OperationIconWrapper,
  Styles,
} from "./styled";

type IProps = {
  isCollapse: boolean;
  setIsCollapse: Dispatch<SetStateAction<boolean>>;
  divHeight: number;
};

export const formatCompanyName = (name: string | undefined): string => {
  if (!name) return "";
  const maxCharsPerLine = 15;
  const maxLines = 2;
  const maxTotalChars = maxCharsPerLine * maxLines; // 30 characters

  // If <= 15 characters, display 1 line
  if (name.length <= maxCharsPerLine) {
    return name;
  }

  // If > 15 characters, display max 2 lines
  const firstLine = name.substring(0, maxCharsPerLine);

  if (name.length <= maxTotalChars) {
    // If <= 30 characters, display 2 full lines
    const secondLine = name.substring(maxCharsPerLine);
    return `${firstLine}\n${secondLine}`;
  } else {
    // If > 30 characters, display 2 lines and add "..."
    const secondLine = name.substring(maxCharsPerLine, maxTotalChars);
    return `${firstLine}\n${secondLine}...`;
  }
};

const SideBar = ({
  isCollapse: originalIsCollapse,
  setIsCollapse,
  divHeight,
}: IProps) => {
  const dispatch = useDispatch();
  const { confirmingState, setConfirmingState, setCallbackEvent } =
    useConfirmingLeave();
  const { isUserResigned, auth } = useGetAuth();
  const isBannerVisible = useSelector(getIsBannerVisible);
  // Check if user is admin
  // Only check admin, no longer check payment status
  const { navItems, subOperationItems, subAppStoreItems } = useNavItem();
  const location = useLocation();
  const companyId = getCompanyId();

  const pathName = location?.pathname?.split("/")[1];
  const fullPathName = location?.pathname;

  const navigate = useNavigate();
  const toAbsolute = (path: string) =>
    path.startsWith("/") ? path : `/${path}`;
  const isCollapse = isMobileOnly ? false : originalIsCollapse;

  const [isSubSalaryNav, setIsSubSalaryNav] = useState<boolean>(true);
  const [isSubBusinessNav, setIsSubBusinessNav] = useState<boolean>(true);
  const [isSubPersonalsNav, setIsSubPersonalNav] = useState<boolean>(true);
  const [isSubOperationsNav, setIsSubOperationNav] = useState<boolean>(false);
  const [isSubOrganizationNav, setIsSubOrganizationNav] =
    useState<boolean>(true);
  const [isSubAppStoresNav, setIsSubAppStoreNav] = useState<boolean>(true);
  const [isSubAppaisalNav, setIsSubAppraisalNav] = useState<boolean>(true);
  const [isOpenByHover, setIsOpenByHover] = useState(false);
  const [isSubManualNav, setIsSubManualNav] = useState<boolean>(false);
  const [isSubPermissionSalaryNav, setIsSubPermissionSalaryNav] =
    useState<boolean>(false);
  const [isSubCompanyNav, setIsSubCompanyNav] = useState<boolean>(true);
  const { maintenanceNotice } = useReAuthenticationOneTime();
  const [maintenanceBanner, setMaintenanceBanner] = useState<{
    message: string;
    backgroundColor: string;
  } | null>(null);
  const listMenuRef = useRef<HTMLUListElement>(null);

  const [searchParams] = useSearchParams();
  const isDisplayInNewTab = getEffectiveIsDisplayInNewTab(
    searchParams.get(IS_DISPLAY_IN_NEW_TAB_PARAM) === "true",
  );

  // const subMeneSalaryPatch = [
  //   ROUTES.ANNUAL_SALARY_LIST,
  //   ROUTES.ANNUAL_SALARY_SUMMARIES,
  //   // ROUTES.SALARY_CONFIGURATION,
  //   ROUTES.ANNUAL_PAYMENT_LIST,
  //   ROUTES.ANNUAL_PAYMENT_SUMMARIES,
  // ];
  // const subMenuBusinessPatch = [
  //   ROUTES.CUSTOMER_LIST,
  //   ROUTES.CUSTOMER_HISTORY_SUMMARIES,
  //   ROUTES.PROJECT_MANAGE,
  //   // ROUTES.APPS,
  //   ROUTES.SETTING_PRIVILEGE,
  // ];
  const subMenuOrganizationPatch = [
    ROUTES.ORGANIZATION_CHART,
    ROUTES.WORKERS,
    ROUTES.USERS,
  ];

  const subMenuPersonalPatch = [
    ROUTES.BRANCH,
    ROUTES.ORGANIZATIONS,
    ROUTES.DEPARTMENTS,
    ROUTES.ORGS,
    ROUTES.DIVISIONS,
    ROUTES.BRANCH_CHART,
    // ROUTES.PAYSLIP,
    ROUTES.AUTH,
    ROUTES.MANAGEMENT,
    // ROUTES.ORGANIZATION_MANAGEMENT,
    ROUTES.POSITIONS,
  ];
  const subMenuOperationPatch = [
    ROUTES.COMPANIES,
    ROUTES.ADMIN_CONFIG,
    ROUTES.APP_CATEGORIES,
    ROUTES.APP_SUBMITTED_MANAGEMENT,
  ];
  const subMenuAppraisalPatch = [
    ROUTES.APPRAISAL_LIST,
    ROUTES.APPRAISAL_SHEET_LIST,
    ROUTES.APPRAISAL_ANALYST,
    ROUTES.APPRAISAL_KGI_LIST,
    ROUTES.APPRAISAL_SETTING,
    ROUTES.PERFORMANCE_APPRAISAL,
    ROUTES.APPRAISAL_SUMMARIES,
  ];
  const subAppStorePatch = [
    ROUTES.APP_CATEGORIES,
    ROUTES.APP_SUBMITTED_MANAGEMENT,
  ];
  // const subPermissionsSalaryPatch = [
  //   ROUTES.PERMISSIONS_MANAGEMENT,
  //   ROUTES.ORGANIZATION_MANAGEMENT,
  //   // ROUTES.COMPANY_CONFIGURATION,
  // ];

  // const subProjectManagerPatch = [
  //   ROUTES.PROJECT_MANAGER,
  //   ROUTES.PROJECT_MANAGER_LIST,
  //   ROUTES.PROJECT_MANAGER_DETAIL,
  //   ROUTES.PROJECT_PHASE,
  // ];

  const subAppsPatch = [ROUTES.APPS, ROUTES.BIZ_SHEET_SUMMARIES];

  // useEffect(() => {
  //   if (document.getElementsByClassName("hide-box")[0]) {
  //     if (
  //       window.location.href.includes("/apps") &&
  //       !window.location.href.includes("/apps/create") &&
  //       !window.location.href.includes("/records") &&
  //       !matchAppDetail &&
  //       !matchAppDetailPermission
  //     ) {
  //       (
  //         document.getElementsByClassName("hide-box")[0] as HTMLElement
  //       ).style.backgroundColor = "rgb(240, 242, 245)";
  //     } else {
  //       (
  //         document.getElementsByClassName("hide-box")[0] as HTMLElement
  //       ).style.backgroundColor = "transparent";
  //     }
  //   }
  // }, [location, isCollapse, matchAppDetail, matchAppDetailPermission]);

  const nameCompany = useMemo(() => {
    return (
      auth?.companies.find((company) => company.id.toString() == companyId)
        ?.name ||
      auth?.companies.find((company) => company.is_work_default)?.name
    );
  }, [auth, companyId]);
  const permission = useGetPermissions();

  // const isTwoLines = useMemo((): boolean => {
  //   if (!nameCompany) return false;
  //   return (nameCompany as string).length > 15;
  // }, [nameCompany]);

  useEffect(() => {
    if (maintenanceNotice) {
      const now = new Date();
      const maintenanceStart = new Date(maintenanceNotice.maintenance_start_at);
      const maintenanceEnd = new Date(maintenanceNotice.maintenance_end_at);
      const advanceStart = new Date(maintenanceNotice.advance_start_at);
      const completionEnd = new Date(maintenanceNotice.completion_end_at);

      if (
        now >= maintenanceStart &&
        now <= maintenanceEnd &&
        maintenanceNotice?.is_show_main_notice &&
        maintenanceNotice.main_notice
      ) {
        setMaintenanceBanner({
          message: maintenanceNotice.main_notice,
          backgroundColor: "#ED1C24",
        });
      } else if (
        now >= advanceStart &&
        now < maintenanceStart &&
        maintenanceNotice?.is_show_advance_notice &&
        maintenanceNotice?.advance_notice
      ) {
        setMaintenanceBanner({
          message: maintenanceNotice.advance_notice,
          backgroundColor: "#FF9500",
        });
      } else if (
        now > maintenanceEnd &&
        now <= completionEnd &&
        maintenanceNotice?.is_show_completion_notice &&
        maintenanceNotice?.completion_notice
      ) {
        setMaintenanceBanner({
          message: maintenanceNotice.completion_notice,
          backgroundColor: "#34C759",
        });
      } else {
        setMaintenanceBanner(null);
      }
    }
  }, [maintenanceNotice]);

  useEffect(() => {
    const handleLayoutUpdate = () => {
      const heightDf = "48px";
      const heightDfNum = 48;

      const wrapperSideBarCompany = document.getElementById(
        "wrapper-side_bar_company",
      );
      const containerSideBarCompany: any = wrapperSideBarCompany?.querySelector(
        ".container-side_bar_company",
      );
      const antSpinNestedLoading: any = wrapperSideBarCompany?.querySelector(
        ".ant-spin-nested-loading",
      );
      const sideBar = document.getElementById("side-bar");
      const listMenu: any = sideBar?.querySelector(".list-menu");
      const containerMain = document.getElementById("container_main");
      const mainLayout = document.getElementById("main-layout");
      const containerBoxCollapse = document.getElementById(
        "container-box-collapse",
      );

      if (maintenanceBanner) {
        const header = document.getElementById("app-header");
        if (header) {
          // Banner creation logic is handled in the main effect body below
          // Here we just calculate heights assuming banner is there
          const heightHeader =
            header.offsetHeight > 86 ? header.offsetHeight : 86;
          const heightHasBn = `${heightHeader}px`;

          if (wrapperSideBarCompany) {
            wrapperSideBarCompany.style.top = heightHasBn;
            if (!isMobileOnly) {
              wrapperSideBarCompany.style.height = `calc(100dvh - ${heightHeader}px)`;
            } else {
              wrapperSideBarCompany.style.height = "";
            }
          }
          if (sideBar) {
            sideBar.style.top = heightHasBn;
            if (!isMobileOnly) {
              sideBar.style.height = `calc(100dvh - ${heightHeader}px)`;
            } else {
              sideBar.style.height = "";
            }
          }
          if (containerBoxCollapse) {
            containerBoxCollapse.style.top = heightHasBn;
          }
          if (containerMain) {
            containerMain.style.paddingTop = heightHasBn;
          }
          if (mainLayout) {
            mainLayout.style.minHeight = `calc(100dvh - ${heightHeader}px)`;
            mainLayout.style.height = `calc(100dvh - ${heightHeader}px)`;
            handleReactgrid(mainLayout, heightHeader, 0);
            handleReactgrid(mainLayout, heightHeader - 20, 0, "reactgrid2");
          }
          if (containerSideBarCompany) {
            if (!isMobileOnly) {
              containerSideBarCompany.style.height = `calc(100dvh - ${heightHeader}px)`;
            } else {
              containerSideBarCompany.style.height = "";
            }
          }
          if (listMenu) {
            listMenu.style.height = ""; // Reset inline height để CSS height: 100% hoạt động dựa trên flex wrapper
          }
          if (antSpinNestedLoading) {
            antSpinNestedLoading.style.top = heightHasBn;
          }
        }
      } else {
        if (wrapperSideBarCompany) {
          wrapperSideBarCompany.style.top = heightDf;
          if (!isMobileOnly) {
            wrapperSideBarCompany.style.height = `calc(100dvh - ${heightDfNum}px)`;
          } else {
            wrapperSideBarCompany.style.height = "";
          }
        }
        if (sideBar) {
          sideBar.style.top = heightDf;
          if (!isMobileOnly) {
            sideBar.style.height = `calc(100dvh - ${heightDfNum}px)`;
          } else {
            sideBar.style.height = "";
          }
        }
        if (containerMain) {
          containerMain.style.paddingTop = heightDf;
        }
        if (containerBoxCollapse) {
          containerBoxCollapse.style.top = heightDf;
        }
        if (mainLayout) {
          mainLayout.style.minHeight = `calc(100dvh - ${heightDfNum}px)`;
          mainLayout.style.height = `calc(100dvh - ${heightDfNum}px)`;
          handleReactgridDf(mainLayout, heightDfNum, 0);
          handleReactgridDf(mainLayout, heightDfNum - 20, 0, "reactgrid2");
        }
        if (containerSideBarCompany) {
          if (!isMobileOnly) {
            containerSideBarCompany.style.height = `calc(100dvh - 55px)`;
          } else {
            containerSideBarCompany.style.height = "";
          }
        }
        if (listMenu) {
          listMenu.style.height = ""; // Reset inline height để CSS height: 100% hoạt động
        }
        if (antSpinNestedLoading) {
          antSpinNestedLoading.style.top = heightDf;
        }
      }
    };

    // Run layout update initially
    handleLayoutUpdate();

    // Attach resize listener to handle mobile address bar hide/show
    window.addEventListener("resize", handleLayoutUpdate);

    // Maintenance banner DOM logic
    let banner: HTMLDivElement | null = null;
    const header = document.getElementById("app-header");

    if (maintenanceBanner && header) {
      banner = document.createElement("div");
      banner.id = "maintenance-banner";
      banner.style.backgroundColor = maintenanceBanner.backgroundColor;
      banner.style.color = "white";
      banner.style.padding = "8px 20px";
      banner.style.display = "flex";
      banner.style.minHeight = "38px";

      const iconContainer = document.createElement("div");
      iconContainer.style.marginRight = "10px";
      iconContainer.style.display = "flex";
      iconContainer.style.alignItems = "center";

      const svg = document.createElementNS("http://www.w3.org/2000/svg", "svg");
      svg.setAttribute("width", "24");
      svg.setAttribute("height", "21");
      svg.setAttribute("viewBox", "0 0 24 21");
      svg.setAttribute("fill", "none");

      const path1 = document.createElementNS(
        "http://www.w3.org/2000/svg",
        "path",
      );
      path1.setAttribute(
        "d",
        "M10.3405 1.63497L1.02497 17.7687C0.856703 18.0601 0.768098 18.3906 0.768066 18.727C0.768035 19.0635 0.856579 19.394 1.0248 19.6854C1.19301 19.9768 1.43497 20.2188 1.72636 20.387C2.01774 20.5552 2.34827 20.6437 2.68472 20.6437H21.3142C21.6507 20.6437 21.9812 20.5552 22.2726 20.387C22.564 20.2188 22.8059 19.9768 22.9742 19.6854C23.1424 19.394 23.2309 19.0635 23.2309 18.727C23.2309 18.3906 23.1422 18.0601 22.974 17.7687L13.6595 1.63497C13.4913 1.34367 13.2494 1.10178 12.9581 0.933598C12.6668 0.765419 12.3363 0.67688 12 0.67688C11.6636 0.67688 11.3332 0.765419 11.0419 0.933598C10.7506 1.10178 10.5087 1.34367 10.3405 1.63497Z",
      );
      path1.setAttribute("fill", "#FFB400");

      const path2 = document.createElementNS(
        "http://www.w3.org/2000/svg",
        "path",
      );
      path2.setAttribute(
        "d",
        "M12.1215 6.61096H11.8785C11.2802 6.61096 10.7952 7.09595 10.7952 7.69421V12.872C10.7952 13.4702 11.2802 13.9552 11.8785 13.9552H12.1215C12.7197 13.9552 13.2047 13.4702 13.2047 12.872V7.69421C13.2047 7.09595 12.7197 6.61096 12.1215 6.61096Z",
      );
      path2.setAttribute("fill", "#FCF4D9");

      const path3 = document.createElementNS(
        "http://www.w3.org/2000/svg",
        "path",
      );
      path3.setAttribute(
        "d",
        "M12 18.0617C12.6653 18.0617 13.2047 17.5223 13.2047 16.857C13.2047 16.1916 12.6653 15.6522 12 15.6522C11.3346 15.6522 10.7952 16.1916 10.7952 16.857C10.7952 17.5223 11.3346 18.0617 12 18.0617Z",
      );
      path3.setAttribute("fill", "#FCF4D9");

      svg.appendChild(path1);
      svg.appendChild(path2);
      svg.appendChild(path3);
      iconContainer.appendChild(svg);

      const textElement = document.createElement("div");
      textElement.textContent = maintenanceBanner.message;
      textElement.style.flex = "1";

      banner.appendChild(iconContainer);
      banner.appendChild(textElement);
      header.prepend(banner);

      // Re-run layout update after DOM change
      handleLayoutUpdate();

      setTimeout(() => {
        dispatch(setBannerVisibility({ show: true }));
      }, 50);
    } else {
      setTimeout(() => {
        dispatch(setBannerVisibility({ show: false }));
      }, 50);
    }

    return () => {
      window.removeEventListener("resize", handleLayoutUpdate);
      if (banner && header?.contains(banner)) {
        header.removeChild(banner);
      }
    };
  }, [maintenanceBanner]);

  // Auto-expand 権限管理 menu when navigating to permissions_management
  useEffect(() => {
    if (
      fullPathName?.includes(ROUTES.PERMISSIONS_MANAGEMENT) ||
      location.pathname?.includes(ROUTES.PERMISSIONS_MANAGEMENT)
    ) {
      setIsSubPermissionSalaryNav(true);
    }

    if (
      subMenuOperationPatch.some(
        (path) =>
          fullPathName?.includes(path) || location.pathname?.includes(path),
      )
    ) {
      setIsSubOperationNav(true);
    }
  }, [fullPathName, location.pathname]);

  const handleReactgrid = (
    mainLayout: any,
    height: any,
    count: any = 0,
    className = "reactgrid",
  ) => {
    setTimeout(() => {
      const reactgrid: any = mainLayout?.querySelector(`.${className}`);
      if (!reactgrid?.closest(".not-set-height")) return;
      if (reactgrid) {
        const newStyles = {
          height: `calc(100vh - ${132 + height}px) !important`,
        };
        updateClassStyle(className, newStyles);
      } else {
        if (count < 5) {
          handleReactgrid(mainLayout, height, count + 1);
        }
      }
    }, 500);
  };

  const handleReactgridDf = (
    mainLayout: any,
    height: any,
    count: any = 0,
    className = "reactgrid",
  ) => {
    setTimeout(() => {
      const reactgrid: HTMLElement = mainLayout?.querySelector(`.${className}`);
      if (!reactgrid?.closest(".not-set-height")) return;
      if (reactgrid) {
        const newStyles = {
          height: `calc(100vh - ${132 + height}px) !important`,
        };
        updateClassStyle(className, newStyles);
      } else {
        if (count < 5 && !maintenanceBanner) {
          handleReactgrid(mainLayout, height, count + 1, className);
        }
      }
    }, 500);
  };

  const updateClassStyle = (className: any, newStyles: any) => {
    let styleSheet: any = Array.from(document.styleSheets).find((sheet) => {
      return Array.from(sheet.cssRules).some(
        (rule: any) => rule.selectorText === `.${className}`,
      );
    });

    if (!styleSheet) {
      // Nếu không tìm thấy, tạo thẻ <style> mới
      const styleTag = document.createElement("style");
      document.head.appendChild(styleTag);
      styleSheet = styleTag.sheet;
    }

    // Tìm và xóa quy tắc cũ
    const ruleIndex = Array.from(styleSheet.cssRules).findIndex(
      (rule: any) => rule.selectorText === `.${className}`,
    );
    if (ruleIndex !== -1) {
      styleSheet.deleteRule(ruleIndex);
    }

    // Thêm quy tắc mới
    const styleString = Object.entries(newStyles)
      .map(
        ([key, value]) =>
          `${key.replace(/[A-Z]/g, (m) => `-${m.toLowerCase()}`)}: ${value}`,
      )
      .join("; ");
    styleSheet.insertRule(
      `.${className} { ${styleString} }`,
      styleSheet.cssRules.length,
    );
  };

  const handleToggle = (
    e: React.MouseEvent,
    toggleFn: (value: boolean) => void,
    current: boolean,
  ) => {
    e.stopPropagation();
    toggleFn(!current);
  };

  const toggleNav = (toggleFn: (value: boolean) => void, current: boolean) => {
    toggleFn(!current);
  };

  const renderChevron = (
    isOpen: boolean,
    toggleFn: (value: boolean) => void,
    current: boolean,
  ) => (
    <p onClick={(e) => handleToggle(e, toggleFn, current)}>
      {isOpen ? <FaChevronLeft /> : <FaChevronRight />}
    </p>
  );

  const getGroupClass = (navItem: any) =>
    navItem.groupType && navItem.subGroupType
      ? `nav-group-item nav-group-${navItem.subGroupType}`
      : "";

  const getIsActive = (navItem: any) =>
    // Active cho 人事評価 nếu ở các route liên quan (dùng constant ROUTES)
    (navItem?.id === 16 &&
      ([
        ROUTES.APPRAISAL_SUMMARIES,
        ROUTES.APPRAISAL_SETTING,
        ROUTES.APPRAISAL_LIST,
        ROUTES.APPRAISAL_KGI_LIST,
        ROUTES.APPRAISAL_SHEET_LIST,
      ].includes(pathName) ||
        [
          `/${ROUTES.APPRAISAL_SUMMARIES}`,
          `/${ROUTES.APPRAISAL_SETTING}`,
          `/${ROUTES.APPRAISAL_LIST}`,
          `/${ROUTES.APPRAISAL_KGI_LIST}`,
          `/${ROUTES.APPRAISAL_SHEET_LIST}`,
        ].includes(fullPathName))) ||
    (pathName === ROUTES.COMPANY_POLICIES_CHART &&
      navItem?.navigate === ROUTES.COMPANY_POLICIES_CHART) ||
    (fullPathName?.startsWith(`/${ROUTES.COMPANY_POLICIES}`) &&
      (navItem?.navigate === ROUTES.COMPANY_POLICIES_CHART ||
        navItem?.navigate === ROUTES.COMPANY_POLICIES)) ||
    (pathName === ROUTES.BUSINESS_CONTROLS_CHART &&
      navItem?.navigate === ROUTES.BUSINESS_CONTROLS_CHART) ||
    (pathName === ROUTES.ROLE_PERMISSIONS &&
      navItem?.navigate === ROUTES.ROLE_PERMISSIONS) ||
    (pathName === ROUTES.BUSINESS_MANUALS &&
      navItem?.navigate === ROUTES.BUSINESS_MANUALS) ||
    (pathName === ROUTES.ANNUAL_SALARY_SUMMARIES &&
      navItem?.navigate === ROUTES.ANNUAL_SALARY_SUMMARIES) ||
    (subMenuOrganizationPatch.includes(pathName) &&
      navItem?.subOrganizationItems) ||
    (subMenuPersonalPatch.includes(pathName) && navItem?.subPersonalItems) ||
    (subMenuOperationPatch.includes(pathName) && navItem?.subOperationItems) ||
    (subAppStorePatch.includes(pathName) && navItem?.subAppStoreItems) ||
    (subMenuAppraisalPatch.includes(pathName) && navItem?.subAppraisalItems) ||
    (subMenuOperationPatch.includes(fullPathName) &&
      navItem?.subOperationItems) ||
    (subAppsPatch.includes(pathName) && navItem?.subAppItems) ||
    (pathName === "financial-report" &&
      navItem?.navigate === "financial-report") ||
    (pathName === ROUTES.PROJECT_MANAGE &&
      navItem?.navigate === ROUTES.PROJECT_MANAGE) ||
    (pathName === ROUTES.PROJECT_MANAGER &&
      navItem?.navigate === ROUTES.PROJECT_MANAGER) ||
    (fullPathName?.startsWith(`/${ROUTES.BUSINESS_RPG}`) &&
      navItem?.navigate === ROUTES.BUSINESS_RPG) ||
    ((pathName === ROUTES.CUSTOMER_LIST ||
      pathName === ROUTES.CUSTOMER_HISTORY_SUMMARIES ||
      fullPathName?.startsWith(`/${ROUTES.CUSTOMER_LIST}`)) &&
      navItem?.navigate === ROUTES.CUSTOMER_HISTORY_SUMMARIES) ||
    (pathName === ROUTES.ELECTRONIC_LEARNING_CHART &&
      navItem?.navigate === ROUTES.ELECTRONIC_LEARNING_CHART) ||
    (fullPathName?.startsWith(`/${ROUTES.Electronic_Learning}`) &&
      (navItem?.navigate === ROUTES.ELECTRONIC_LEARNING_CHART ||
        navItem?.navigate === ROUTES.Electronic_Learning)) ||
    (pathName === ROUTES.CORRELATION_DIAGRAM &&
      navItem?.navigate === ROUTES.CORRELATION_DIAGRAM) ||
    (pathName === "tracking_time" &&
      navItem?.navigate === ROUTES.TRACKING_TIME) ||
    (pathName === ROUTES.CONTRACT_MANAGEMENT &&
      navItem?.navigate === ROUTES.CONTRACT_MANAGEMENT) ||
    (pathName === ROUTES.ANNUAL_ORG_SUMMARIES &&
      navItem?.navigate === ROUTES.ANNUAL_ORG_SUMMARIES) ||
    (pathName === ROUTES.PERMISSION_SUMMARIES &&
      navItem?.navigate === ROUTES.PERMISSION_SUMMARIES) ||
    ((pathName === ROUTES.PERMISSION_SUMMARIES ||
      pathName === ROUTES.PERMISSIONS_MANAGEMENT ||
      fullPathName?.includes(ROUTES.PERMISSIONS_MANAGEMENT)) &&
      navItem?.navigate === ROUTES.PERMISSION_SUMMARIES) ||
    (navItem?.id === 2 &&
      (pathName === ROUTES.WORKERS ||
        pathName === ROUTES.PERSONNEL ||
        pathName === ROUTES.ORGANIZATION_CHART ||
        fullPathName?.startsWith(`/${ROUTES.WORKERS}`))) ||
    (pathName === ROUTES.PROJECT_CHART &&
      navItem?.navigate === ROUTES.PROJECT_CHART) ||
    (fullPathName?.startsWith(`/${ROUTES.PROJECT_MANAGER}`) &&
      navItem?.navigate === ROUTES.PROJECT_CHART) ||
    (pathName === ROUTES.CORRELATION_DIAGRAM &&
      navItem?.navigate === ROUTES.CORRELATION_DIAGRAM);

  const heightMaintenanceBanner =
    document.getElementById("maintenance-banner")?.offsetHeight;

  useLayoutEffect(() => {
    let observer: MutationObserver | null = null;
    let timeoutId: number;
    const mainLayoutEl = document.getElementById("main-layout");
    const bannerHeight = heightMaintenanceBanner || 0;
    const maxWaitMs = 15 * 60 * 1000;

    const checkHasTableMain = () => {
      return new Promise<HTMLElement>((resolve) => {
        const existingEl = document.querySelector(".table-main") as HTMLElement;
        if (existingEl) {
          resolve(existingEl);
          return;
        }

        observer = new MutationObserver(() => {
          const tableMainEl = document.querySelector(
            ".table-main",
          ) as HTMLElement;
          if (tableMainEl) {
            observer?.disconnect();
            clearTimeout(timeoutId);
            resolve(tableMainEl);
          }
        });
        if (!mainLayoutEl) return;
        observer.observe(mainLayoutEl, {
          childList: true,
          subtree: true,
        });
        timeoutId = window.setTimeout(() => {
          observer?.disconnect();
        }, maxWaitMs);
      });
    };
    const setTableHeight: () => Promise<void> = async () => {
      try {
        const tableMainEl = await checkHasTableMain();
        const baseHeight = 180;
        const offset = isBannerVisible ? bannerHeight + baseHeight : baseHeight;
        tableMainEl.style.maxHeight = `calc(100vh - ${offset}px)`;
      } catch (error) {
        console.error("Error setting table height:", error);
      }
    };
    setTableHeight();
    return () => {
      observer?.disconnect();
      clearTimeout(timeoutId);
    };
  }, [location.pathname, isBannerVisible, heightMaintenanceBanner]);

  if (isDisplayInNewTab) return <></>;

  return isCollapse ? (
    <CollapsedSidebar
      isCollapse={isCollapse}
      setIsCollapse={setIsCollapse}
      setIsOpenByHover={setIsOpenByHover}
      setIsSubBusinessNav={setIsSubBusinessNav}
      setIsSubPersonalNav={setIsSubPersonalNav}
      setIsSubOperationNav={setIsSubOperationNav}
      setIsSubAppStoreNav={setIsSubAppStoreNav}
      setIsSubManualNav={setIsSubManualNav}
      setIsSubAppraisalNav={setIsSubAppraisalNav}
      setIsSubPermissionSalaryNav={setIsSubPermissionSalaryNav}
      setIsSubOrganizationNav={setIsSubOrganizationNav}
      setIsSubSalaryNav={setIsSubSalaryNav}
      setIsSubCompanyNav={setIsSubCompanyNav}
      divHeight={divHeight}
      maintenanceBanner={maintenanceBanner}
    />
  ) : (
    <Styles
      className="side-bar"
      id="side-bar"
      onClick={() => setIsOpenByHover(false)}
      divHeight={divHeight}
      isCollapse={isCollapse}
      isMobile={isMobileOnly}
      style={{
        zIndex: isOpenByHover ? 101 : 90,
        top:
          (isCollapse ? divHeight + 48 : 48) +
          (maintenanceBanner ? heightMaintenanceBanner || 0 : 0),
      }}
    >
      {!isMobileOnly && (
        <div className="collapse-box">
          <ButtonVerticalCollapse
            directionArrow="left"
            onClick={() => setIsCollapse(true)}
            directionRadius="right"
            variant="danger"
            style={{ minHeight: 52 }}
          >
            閉
          </ButtonVerticalCollapse>
        </div>
      )}

      <div
        style={{
          display: "flex",
          position: "relative",
          flex: "1 1 0",
          minHeight: 0,
          overflow: "hidden",
        }}
      >
        <CustomVerticalScrollBar element={listMenuRef.current} isCustom />

        <ListMenu
          ref={listMenuRef}
          className="list-menu"
          isCollapse={isCollapse}
          isMobile={isMobileOnly}
          maintenanceBannerHeight={
            maintenanceBanner ? heightMaintenanceBanner || 0 : 0
          }
          onMouseLeave={() => {
            if (isOpenByHover) {
              setIsOpenByHover(false);
            }
          }}
          onClick={(e) => {
            const target = e.target as HTMLElement;
            const link = target.closest("a");
            if (isMobileOnly && link) {
              if (link.getAttribute("data-greyed-out") === "true") {
                return;
              }
              dispatch(setIsMobileMenuOpen(false));
            }
          }}
        >
          <li className="menu-text">メニュー</li>
          {navItems.map((item: any, index) => {
            // Handle group headers
            const isCompanyRulesMenu = item.id === MENU_ITEM_IDS.COMPANY_RULES;
            const isDevelopment = item.isDevelopment || false;
            // Check if user has no permission for this menu item
            const hasNoPermission = item.hasNoPermission || false;
            const shouldGreyOutCompanyRules =
              isCompanyRulesMenu || isDevelopment;
            const disabledTooltipMessage = item.disabledTooltipMessage;
            const isActive = getIsActive(item);
            // Determine if this item is part of a group
            const groupClass = getGroupClass(item);
            if (item.isSubGroupHeader) {
              const displayTitle = isMobileOnly
                ? item.title.replace("◇ ", "")
                : item.title;
              return (
                <Fragment key={item.id ?? `subgroup-${index}`}>
                  <li
                    className={`nav-group-header ${
                      isMobileOnly ? "mobile" : ""
                    }`}
                  >
                    <div className="nav-group-title">{displayTitle}</div>
                  </li>
                  <ul
                    className={`parent-tab${
                      item?.childrenTabs?.length === 1 ? "-only" : ""
                    }`}
                  >
                    {item?.childrenTabs?.map((tab: any, index: number) => {
                      const tabHasNoPermission = tab.hasNoPermission || false;
                      const tabIsActive = getIsActive(tab);
                      const tabGroupClass = getGroupClass(tab);
                      return (
                        <li
                          key={tab.id ?? tab.navigate ?? index}
                          className={tabGroupClass}
                        >
                          <MenuItemLink
                            onClick={(e) => {
                              if (tabHasNoPermission) {
                                if (tab.noPermissionOnClickMessage) {
                                  notification.warning({
                                    message: tab.noPermissionOnClickMessage,
                                  });
                                }
                                return;
                              } else {
                                if (isMobileOnly) {
                                  dispatch(setIsMobileMenuOpen(false));
                                }
                                navigate(toAbsolute(tab.navigate));
                              }
                            }}
                            marginBottom={tab.mb}
                            isGreyedOut={tabHasNoPermission}
                            data-greyed-out={
                              tabHasNoPermission ? "true" : "false"
                            }
                            data-testid={tab.dataTestId}
                            className={`${tabIsActive ? "active" : ""} ${
                              tab.navigate === ROUTES.ORGANIZATION_MANAGEMENT
                                ? "nav-permission-management"
                                : tab.navigate === ROUTES.TRACKING_TIME ||
                                  tab.navigate === ROUTES.CORRELATION_DIAGRAM ||
                                  tab.navigate === ROUTES.CONTRACT_MANAGEMENT ||
                                  tab.navigate === ROUTES.PERMISSION_SUMMARIES
                                ? "nav-single-tab"
                                : ""
                            }`}
                          >
                            {tabHasNoPermission ? (
                              <SharedTooltip
                                title={
                                  isUserResigned
                                    ? TOOLTIP_MESSAGES.USER_RETIRED
                                    : tab.disabledTooltipMessage ||
                                      TOOLTIP_MESSAGES.NOT_PURCHASE
                                }
                              >
                                <MenuItemContent>
                                  {tab?.icon ||
                                  (tab?.iconActive && tab?.iconNotActive) ? (
                                    <span>
                                      {tab?.icon ||
                                        (tabIsActive
                                          ? tab?.iconActive
                                          : tab?.iconNotActive)}
                                    </span>
                                  ) : (
                                    <></>
                                  )}
                                  <MenuItemTitle>{tab.title}</MenuItemTitle>
                                </MenuItemContent>
                              </SharedTooltip>
                            ) : (
                              <>
                                {tab?.icon ||
                                (tab?.iconActive && tab?.iconNotActive) ? (
                                  <span>
                                    {tab?.icon ||
                                      (tabIsActive
                                        ? tab?.iconActive
                                        : tab?.iconNotActive)}
                                  </span>
                                ) : (
                                  <></>
                                )}
                                <MenuItemTitle>{tab.title}</MenuItemTitle>
                              </>
                            )}
                          </MenuItemLink>
                        </li>
                      );
                    })}
                  </ul>
                </Fragment>
              );
            }

            return (
              <li key={item.id ?? index} className={groupClass}>
                {item.id && item.navigate && (
                  <>
                    {item.navigate === ROUTES.MANUAL ? (
                      <MenuItemLink
                        onClick={(e) => {
                          if (hasNoPermission || shouldGreyOutCompanyRules) {
                            e.preventDefault();
                            return;
                          }

                          if (item.subSalaryItems) {
                            setIsSubSalaryNav(!isSubSalaryNav);
                            toggleNav(setIsSubSalaryNav, isSubSalaryNav);
                          } else if (item?.subOrganizationItems) {
                            toggleNav(
                              setIsSubOrganizationNav,
                              isSubOrganizationNav,
                            );
                          } else if (item.subPersonalItems) {
                            toggleNav(setIsSubPersonalNav, isSubPersonalsNav);
                          } else if (item.subOperationItems) {
                            toggleNav(setIsSubOperationNav, isSubOperationsNav);
                          } else if (item.subAppStoreItems) {
                            toggleNav(setIsSubAppStoreNav, isSubAppStoresNav);
                          } else if (item.subAppraisalItems) {
                            toggleNav(setIsSubAppraisalNav, isSubAppaisalNav);
                          } else if (item.subManualItems) {
                            toggleNav(setIsSubManualNav, isSubManualNav);
                          } else if (item.subPermissionSalaryItems) {
                            toggleNav(
                              setIsSubPermissionSalaryNav,
                              isSubPermissionSalaryNav,
                            );
                          } else if (item.subCompanyItems) {
                            toggleNav(setIsSubCompanyNav, isSubCompanyNav);
                          } else {
                            navigate(toAbsolute(item.navigate));
                          }
                        }}
                        marginBottom={item.mb}
                        isGreyedOut={
                          shouldGreyOutCompanyRules || hasNoPermission
                        }
                        data-greyed-out={
                          shouldGreyOutCompanyRules || hasNoPermission
                            ? "true"
                            : "false"
                        }
                        data-testid={item.dataTestId}
                        className={`${isActive ? "active" : ""} ${
                          item.navigate === ROUTES.ORGANIZATION_MANAGEMENT
                            ? "nav-permission-management"
                            : item.navigate === ROUTES.TRACKING_TIME ||
                              item.navigate === ROUTES.CORRELATION_DIAGRAM ||
                              item.navigate === ROUTES.CONTRACT_MANAGEMENT ||
                              item.navigate === ROUTES.PERMISSION_SUMMARIES
                            ? "nav-single-tab"
                            : ""
                        }`}
                      >
                        {shouldGreyOutCompanyRules || hasNoPermission ? (
                          <SharedTooltip
                            title={
                              isDevelopment
                                ? TOOLTIP_MESSAGES.DEVELOPMENT
                                : disabledTooltipMessage
                                ? disabledTooltipMessage
                                : hasNoPermission
                                ? isUserResigned
                                  ? TOOLTIP_MESSAGES.USER_RETIRED
                                  : TOOLTIP_MESSAGES.NO_PERMISSION
                                : TOOLTIP_MESSAGES.DEVELOPMENT
                            }
                          >
                            <MenuItemContent>
                              {item?.icon ||
                              (item?.iconActive && item?.iconNotActive) ? (
                                <span>
                                  {item?.icon ||
                                    (isActive
                                      ? item?.iconActive
                                      : item?.iconNotActive)}
                                </span>
                              ) : (
                                <span>
                                  <Catalog
                                    stroke={
                                      shouldGreyOutCompanyRules ||
                                      hasNoPermission
                                        ? "#FFFFFF"
                                        : isActive
                                        ? SIDEBAR_COLORS.TEXT_ACTIVE
                                        : SIDEBAR_COLORS.TEXT_DEFAULT
                                    }
                                    width={18}
                                    height={18}
                                  />
                                </span>
                              )}
                              <MenuItemTitle>{item.title}</MenuItemTitle>
                            </MenuItemContent>
                          </SharedTooltip>
                        ) : (
                          <span>
                            <Catalog
                              stroke={
                                isActive
                                  ? SIDEBAR_COLORS.TEXT_ACTIVE
                                  : SIDEBAR_COLORS.TEXT_DEFAULT
                              }
                              width={18}
                              height={18}
                            />
                          </span>
                        )}
                        <div
                          style={{
                            height: "100%",
                            display: "flex",
                            alignItems: "center",
                          }}
                        >
                          {item.title}
                        </div>

                        {item.subSalaryItems &&
                          renderChevron(
                            isSubSalaryNav,
                            setIsSubSalaryNav,
                            isSubSalaryNav,
                          )}
                        {/* {item.subBusinessItems && (
                        <p
                          onClick={() => setIsSubBusinessNav(!isSubBusinessNav)}
                        >
                          {!isSubBusinessNav ? (
                            <FaChevronUp />
                          ) : (
                            <FaChevronDown />
                          )}
                        </p>
                      )} */}
                        {item.subPersonalItems &&
                          renderChevron(
                            isSubPersonalsNav,
                            setIsSubPersonalNav,
                            isSubPersonalsNav,
                          )}
                        {item?.subOrganizationItems &&
                          renderChevron(
                            isSubOrganizationNav,
                            setIsSubOrganizationNav,
                            isSubOrganizationNav,
                          )}
                        {item.subOperationItems &&
                          renderChevron(
                            isSubOperationsNav,
                            setIsSubOperationNav,
                            isSubOperationsNav,
                          )}
                        {item.subAppStoreItems &&
                          renderChevron(
                            isSubAppStoresNav,
                            setIsSubAppStoreNav,
                            isSubAppStoresNav,
                          )}
                        {item.subAppraisalItems &&
                          renderChevron(
                            isSubAppaisalNav,
                            setIsSubAppraisalNav,
                            isSubAppaisalNav,
                          )}
                        {item.subManualItems &&
                          renderChevron(
                            isSubManualNav,
                            setIsSubManualNav,
                            isSubManualNav,
                          )}
                        {item.subPermissionSalaryItems &&
                          renderChevron(
                            isSubPermissionSalaryNav,
                            setIsSubPermissionSalaryNav,
                            isSubPermissionSalaryNav,
                          )}
                        {item.subCompanyItems &&
                          renderChevron(
                            isSubCompanyNav,
                            setIsSubCompanyNav,
                            isSubCompanyNav,
                          )}
                      </MenuItemLink>
                    ) : (
                      <MenuItemLink
                        onClick={(e) => {
                          if (hasNoPermission || shouldGreyOutCompanyRules) {
                            return;
                          }
                          if (item.subSalaryItems) {
                            setIsSubSalaryNav(!isSubSalaryNav);
                            toggleNav(setIsSubSalaryNav, isSubSalaryNav);
                          } else if (item?.subOrganizationItems) {
                            toggleNav(
                              setIsSubOrganizationNav,
                              isSubOrganizationNav,
                            );
                          } else if (item.subPersonalItems) {
                            toggleNav(setIsSubPersonalNav, isSubPersonalsNav);
                          } else if (item.subOperationItems) {
                            toggleNav(setIsSubOperationNav, isSubOperationsNav);
                          } else if (item.subAppStoreItems) {
                            toggleNav(setIsSubAppStoreNav, isSubAppStoresNav);
                          } else if (item.subAppraisalItems) {
                            toggleNav(setIsSubAppraisalNav, isSubAppaisalNav);
                          } else if (item.subManualItems) {
                            toggleNav(setIsSubManualNav, isSubManualNav);
                          } else if (item.subPermissionSalaryItems) {
                            toggleNav(
                              setIsSubPermissionSalaryNav,
                              isSubPermissionSalaryNav,
                            );
                          } else if (item.subCompanyItems) {
                            toggleNav(setIsSubCompanyNav, isSubCompanyNav);
                          } else {
                            if (isMobileOnly) {
                              dispatch(setIsMobileMenuOpen(false));
                            }
                            navigate(toAbsolute(item.navigate));
                          }
                        }}
                        marginBottom={item.mb}
                        isGreyedOut={
                          shouldGreyOutCompanyRules || hasNoPermission
                        }
                        data-greyed-out={
                          shouldGreyOutCompanyRules || hasNoPermission
                            ? "true"
                            : "false"
                        }
                        data-testid={item.dataTestId}
                        className={`${isActive ? "active" : ""} ${
                          item.navigate === ROUTES.ORGANIZATION_MANAGEMENT
                            ? "nav-permission-management"
                            : item.navigate === ROUTES.TRACKING_TIME ||
                              item.navigate === ROUTES.CORRELATION_DIAGRAM ||
                              item.navigate === ROUTES.CONTRACT_MANAGEMENT ||
                              item.navigate === ROUTES.PERMISSION_SUMMARIES
                            ? "nav-single-tab"
                            : ""
                        }`}
                      >
                        {shouldGreyOutCompanyRules || hasNoPermission ? (
                          <SharedTooltip
                            title={
                              isDevelopment
                                ? TOOLTIP_MESSAGES.DEVELOPMENT
                                : disabledTooltipMessage
                                ? disabledTooltipMessage
                                : hasNoPermission
                                ? isUserResigned
                                  ? TOOLTIP_MESSAGES.USER_RETIRED
                                  : TOOLTIP_MESSAGES.NOT_PURCHASE
                                : TOOLTIP_MESSAGES.DEVELOPMENT
                            }
                          >
                            <MenuItemContent>
                              {item?.icon ||
                              (item?.iconActive && item?.iconNotActive) ? (
                                <span>
                                  {item?.icon ||
                                    (isActive
                                      ? item?.iconActive
                                      : item?.iconNotActive)}
                                </span>
                              ) : (
                                <></>
                              )}
                              <MenuItemTitle>{item.title}</MenuItemTitle>
                            </MenuItemContent>
                          </SharedTooltip>
                        ) : (
                          <>
                            {item?.icon ||
                            (item?.iconActive && item?.iconNotActive) ? (
                              <span>
                                {item?.icon ||
                                  (isActive
                                    ? item?.iconActive
                                    : item?.iconNotActive)}
                              </span>
                            ) : (
                              <></>
                            )}
                            <MenuItemTitle>{item.title}</MenuItemTitle>
                          </>
                        )}
                        {item.subSalaryItems &&
                          renderChevron(
                            isSubSalaryNav,
                            setIsSubSalaryNav,
                            isSubSalaryNav,
                          )}

                        {item?.subOrganizationItems &&
                          renderChevron(
                            isSubOrganizationNav,
                            setIsSubOrganizationNav,
                            isSubOrganizationNav,
                          )}
                        {item.subPersonalItems &&
                          renderChevron(
                            isSubPersonalsNav,
                            setIsSubPersonalNav,
                            isSubPersonalsNav,
                          )}
                        {item.subOperationItems &&
                          renderChevron(
                            isSubOperationsNav,
                            setIsSubOperationNav,
                            isSubOperationsNav,
                          )}
                        {item.subAppStoreItems &&
                          renderChevron(
                            isSubAppStoresNav,
                            setIsSubAppStoreNav,
                            isSubAppStoresNav,
                          )}
                        {item.subAppraisalItems &&
                          renderChevron(
                            isSubAppaisalNav,
                            setIsSubAppraisalNav,
                            isSubAppaisalNav,
                          )}
                        {item.subManualItems &&
                          renderChevron(
                            isSubManualNav,
                            setIsSubManualNav,
                            isSubManualNav,
                          )}
                        {item.subPermissionSalaryItems &&
                          renderChevron(
                            isSubPermissionSalaryNav,
                            setIsSubPermissionSalaryNav,
                            isSubPermissionSalaryNav,
                          )}
                        {item.subCompanyItems &&
                          renderChevron(
                            isSubCompanyNav,
                            setIsSubCompanyNav,
                            isSubCompanyNav,
                          )}
                      </MenuItemLink>
                    )}
                  </>
                )}

                {/* {(item.navigate === ROUTES.WORKERS ||
                  item.navigate === ROUTES.ORGANIZATION_CHART) &&
                  isSubOrganizationNav && (
                    <ul className="sidebar-sub-nav">
                      {subPersonalItems.map((subItem, index) => {
                        const hasNoPermission =
                          subItem.hasNoPermission || false;

                        return (
                          <li key={index}>
                            {subItem?.navigate && (
                              <SharedTooltip
                                title={
                                  hasNoPermission
                                    ? TOOLTIP_MESSAGES.NO_PERMISSION
                                    : ""
                                }
                                show={hasNoPermission}
                              >
                                <SubNavLink
                                  to={toAbsolute(subItem.navigate)}
                                  marginBottom={subItem.mb}
                                  isGreyedOut={hasNoPermission}
                                  data-greyed-out={
                                    hasNoPermission ? "true" : "false"
                                  }
                                  className={
                                    hasNoPermission ? "greyed-out" : ""
                                  }
                                  onClick={(e) => {
                                    if (hasNoPermission) {
                                      e.preventDefault();
                                      e.stopPropagation();
                                      return;
                                    }
                                    if (confirmingState?.status) {
                                      setConfirmingState({
                                        status: Status.OpenModal,
                                      });
                                      setCallbackEvent(
                                        () => () =>
                                          navigate(
                                            toAbsolute(subItem.navigate),
                                          ),
                                      );
                                      e.preventDefault();
                                    } else {
                                      setConfirmingState(null);
                                    }
                                  }}
                                >
                                  {subItem?.icon &&
                                    isCollapse &&
                                    !isOpenByHover && (
                                      <span>{subItem?.icon}</span>
                                    )}
                                  <SubNavSpan>{subItem.title}</SubNavSpan>
                                </SubNavLink>
                              </SharedTooltip>
                            )}
                          </li>
                        );
                      })}
                    </ul>
                  )}

                {item.navigate === ROUTES.POSITIONS && isSubPersonalsNav && (
                  <ul className="sidebar-sub-nav">
                    {subConfigurationItems.map((item, index) => {
                      const hasNoPermission = item.hasNoPermission || false;

                      return (
                        <li key={index}>
                          {item?.navigate && (
                            <SharedTooltip
                              title={
                                hasNoPermission
                                  ? TOOLTIP_MESSAGES.NO_PERMISSION
                                  : ""
                              }
                              show={hasNoPermission}
                            >
                              <SubNavLink
                                to={toAbsolute(item.navigate)}
                                marginBottom={item.mb}
                                isGreyedOut={hasNoPermission}
                                data-greyed-out={
                                  hasNoPermission ? "true" : "false"
                                }
                                className={hasNoPermission ? "greyed-out" : ""}
                                onClick={(e) => {
                                  if (hasNoPermission) {
                                    e.preventDefault();
                                    e.stopPropagation();
                                    return;
                                  }
                                  if (confirmingState?.status) {
                                    setConfirmingState({
                                      status: Status.OpenModal,
                                    });
                                    setCallbackEvent(
                                      () => () =>
                                        navigate(toAbsolute(item.navigate)),
                                    );
                                    e.preventDefault();
                                  } else {
                                    setConfirmingState(null);
                                  }
                                }}
                              >
                                {item?.icon && isCollapse && !isOpenByHover && (
                                  <span>{item?.icon}</span>
                                )}
                                <SubNavSpan>{item.title}</SubNavSpan>
                              </SubNavLink>
                            </SharedTooltip>
                          )}
                        </li>
                      );
                    })}
                    {subManagementItems.map((item: any, index) => (
                      <li key={index}>
                        {item?.navigate && (
                          <SubNavLink
                            to={toAbsolute(item.navigate)}
                            marginBottom={item.mb}
                            onClick={(e) => {
                              if (confirmingState?.status) {
                                setConfirmingState({
                                  status: Status.OpenModal,
                                });
                                setCallbackEvent(
                                  () => () =>
                                    navigate(toAbsolute(item.navigate)),
                                );
                                e.preventDefault();
                              } else {
                                setConfirmingState(null);
                              }
                            }}
                          >
                            {item?.icon && isCollapse && !isOpenByHover && (
                              <span>{item?.icon}</span>
                            )}
                            <SubNavSpan>{item.title}</SubNavSpan>
                          </SubNavLink>
                        )}
                      </li>
                    ))}
                  </ul>
                )}

                {item.subOperationItems && isSubOperationsNav && (
                  <ul className="sidebar-sub-nav">
                    {subOperationItems.map((subItem, index) => {
                      const hasNoPermission = subItem.hasNoPermission || false;

                      return (
                        <li key={index}>
                          {subItem?.navigate && (
                            <SharedTooltip
                              title={
                                hasNoPermission
                                  ? TOOLTIP_MESSAGES.NO_PERMISSION
                                  : ""
                              }
                              show={hasNoPermission}
                            >
                              <SubNavLink
                                to={toAbsolute(subItem.navigate)}
                                marginBottom={subItem.mb}
                                isGreyedOut={hasNoPermission}
                                data-greyed-out={
                                  hasNoPermission ? "true" : "false"
                                }
                                className={hasNoPermission ? "greyed-out" : ""}
                                onClick={(e) => {
                                  if (hasNoPermission) {
                                    e.preventDefault();
                                    e.stopPropagation();
                                    return;
                                  }
                                  if (confirmingState?.status) {
                                    setConfirmingState({
                                      status: Status.OpenModal,
                                    });
                                    setCallbackEvent(
                                      () => () =>
                                        navigate(toAbsolute(subItem.navigate)),
                                    );
                                    e.preventDefault();
                                  } else {
                                    setConfirmingState(null);
                                  }
                                }}
                              >
                                {subItem?.icon &&
                                  isCollapse &&
                                  !isOpenByHover && (
                                    <span>{subItem?.icon}</span>
                                  )}
                                {subItem.title}
                              </SubNavLink>
                            </SharedTooltip>
                          )}
                        </li>
                      );
                    })}

                    {subAppStoreItems.map((subItem, index) => {
                      const hasNoPermission = subItem.hasNoPermission || false;

                      return (
                        <li key={`app-store-${index}`}>
                          {subItem?.navigate && (
                            <SharedTooltip
                              title={
                                hasNoPermission
                                  ? TOOLTIP_MESSAGES.NO_PERMISSION
                                  : ""
                              }
                              show={hasNoPermission}
                            >
                              <SubNavLink
                                to={toAbsolute(subItem.navigate)}
                                marginBottom={subItem.mb}
                                isGreyedOut={hasNoPermission}
                                data-greyed-out={
                                  hasNoPermission ? "true" : "false"
                                }
                                className={hasNoPermission ? "greyed-out" : ""}
                                onClick={(e) => {
                                  if (hasNoPermission) {
                                    e.preventDefault();
                                    e.stopPropagation();
                                    return;
                                  }
                                  if (confirmingState?.status) {
                                    setConfirmingState({
                                      status: Status.OpenModal,
                                    });
                                    setCallbackEvent(
                                      () => () =>
                                        navigate(toAbsolute(subItem.navigate)),
                                    );
                                    e.preventDefault();
                                  } else {
                                    setConfirmingState(null);
                                  }
                                }}
                              >
                                {subItem?.icon &&
                                  isCollapse &&
                                  !isOpenByHover && (
                                    <span>{subItem?.icon}</span>
                                  )}
                                {subItem.title}
                              </SubNavLink>
                            </SharedTooltip>
                          )}
                        </li>
                      );
                    })}
                  </ul>
                )}

                {item.subManualItems && isSubManualNav && canChangeUser && (
                  <ul className="sidebar-sub-nav">
                    {subManualItems.map((item, index) => (
                      <li key={index}>
                        {item?.navigate && (
                          <a
                            target="_blank"
                            rel="noreferrer"
                            style={{ marginBottom: item.mb }}
                            href={
                              "https://drive.google.com/file/d/1zSe4WHEcTCleq6E9n31DBn1O1rvrK-dm/view?usp=sharing"
                            }
                          >
                            {item.title}
                          </a>
                        )}
                      </li>
                    ))}
                  </ul>
                )}

                {item.navigate === ROUTES.ORGANIZATION_MANAGEMENT &&
                  isSubPermissionSalaryNav && (
                    <ul className="sidebar-sub-nav">
                      {subPermissionsSalaryItems.map((subItem, index) => {
                        const parentHasNoPermission =
                          item.hasNoPermission || false;
                        return (
                          <li key={index}>
                            {subItem?.navigate && (
                              <SharedTooltip
                                title={
                                  parentHasNoPermission
                                    ? TOOLTIP_MESSAGES.NO_PERMISSION
                                    : ""
                                }
                                show={parentHasNoPermission}
                              >
                                <SubNavLink
                                  to={toAbsolute(subItem.navigate)}
                                  marginBottom={subItem.mb}
                                  isGreyedOut={
                                    parentHasNoPermission ||
                                    subItem.hasNoPermission
                                  }
                                  data-greyed-out={
                                    parentHasNoPermission ||
                                    subItem.hasNoPermission
                                      ? "true"
                                      : "false"
                                  }
                                  className={
                                    parentHasNoPermission ||
                                    subItem.hasNoPermission
                                      ? "greyed-out"
                                      : ""
                                  }
                                  onClick={(e) => {
                                    if (
                                      parentHasNoPermission ||
                                      subItem.hasNoPermission
                                    ) {
                                      e.preventDefault();
                                      e.stopPropagation();
                                      return;
                                    }
                                    if (confirmingState?.status) {
                                      setConfirmingState({
                                        status: Status.OpenModal,
                                      });
                                      setCallbackEvent(
                                        () => () =>
                                          navigate(
                                            toAbsolute(subItem.navigate),
                                          ),
                                      );
                                      e.preventDefault();
                                    } else {
                                      setConfirmingState(null);
                                    }
                                  }}
                                >
                                  {subItem?.icon &&
                                    isCollapse &&
                                    !isOpenByHover && (
                                      <span>{subItem?.icon}</span>
                                    )}
                                  {subItem.title}
                                </SubNavLink>
                              </SharedTooltip>
                            )}
                          </li>
                        );
                      })}
                    </ul>
                  )} */}
              </li>
            );
          })}
        </ListMenu>
      </div>

      {/* 運営 (Operation) menu underneath 代理店 */}

      <div className="parent-tab-1">
        <SharedTooltip
          title={
            isUserResigned
              ? TOOLTIP_MESSAGES.USER_RETIRED
              : !permission[ROUTES.AGENT_SUMMARIES]
              ? TOOLTIP_MESSAGES.NOT_PURCHASE
              : undefined
          }
          show={!permission[ROUTES.AGENT_SUMMARIES] || isUserResigned}
        >
          <div
            className={`customer-inquiry-button agent ${
              isCollapse ? "collapse" : ""
            } ${
              location.pathname.includes(ROUTES.AGENT_SUMMARIES) ? "active" : ""
            }${
              !permission[ROUTES.AGENT_SUMMARIES] || isUserResigned
                ? "greyed-out"
                : ""
            }`}
            onClick={(e) => {
              if (!permission[ROUTES.AGENT_SUMMARIES] || isUserResigned) {
                e.preventDefault();
                e.stopPropagation();
                return;
              }
              navigate(`/${ROUTES.AGENT_SUMMARIES}`);
              if (isMobileOnly) {
                dispatch(setIsMobileMenuOpen(false));
              }
            }}
            style={{
              cursor:
                !permission[ROUTES.AGENT_SUMMARIES] || isUserResigned
                  ? "not-allowed"
                  : "pointer",
            }}
          >
            <OperationIconWrapper isCollapse={isCollapse}>
              <LeadIcon
                color={
                  !permission[ROUTES.AGENT_SUMMARIES] || isUserResigned
                    ? "#ffffff"
                    : "#333333"
                }
              />
            </OperationIconWrapper>
            {!isCollapse && (
              <CustomerAgentTextWrapper>代理店</CustomerAgentTextWrapper>
            )}
          </div>
        </SharedTooltip>
        <div
          className={`customer-inquiry-button agent ${
            isCollapse ? "collapse" : ""
          }`}
          onClick={(_) => {
            if (isCollapse && !isOpenByHover) {
              return;
            }
            setIsSubOperationNav(!isSubOperationsNav);
          }}
          style={{ cursor: "pointer" }}
        >
          <OperationIconWrapper isCollapse={isCollapse}>
            <SettingIcon
              color={
                [...subOperationItems, ...subAppStoreItems]?.every(
                  (item) => item.hasNoPermission,
                )
                  ? "#ffffff"
                  : "#000000"
              }
            />
          </OperationIconWrapper>
          {!isCollapse && (
            <CustomerAgentTextWrapper
              style={{
                width: "100%",
                justifyContent: "space-between",
                marginLeft: "4px",
              }}
              $disabled={[...subOperationItems, ...subAppStoreItems]?.every(
                (item) => item.hasNoPermission,
              )}
            >
              <span>運営</span>
              {isSubOperationsNav ? (
                <FaChevronUp size={12} />
              ) : (
                <FaChevronDown size={12} />
              )}
            </CustomerAgentTextWrapper>
          )}
        </div>

        {/* Render sub operation items */}
        {isSubOperationsNav && !isCollapse && (
          <div
            style={{
              width: "100%",
              display: "flex",
              flexDirection: "column",
            }}
          >
            {[...subOperationItems, ...subAppStoreItems].map(
              (subItem, index) => {
                const hasNoPermission = subItem.hasNoPermission || false;
                return (
                  <SharedTooltip
                    key={index}
                    title={
                      hasNoPermission
                        ? isUserResigned
                          ? TOOLTIP_MESSAGES.USER_RETIRED
                          : TOOLTIP_MESSAGES.NO_PERMISSION
                        : ""
                    }
                    show={hasNoPermission}
                  >
                    <div
                      className={`customer-inquiry-button agent ${
                        location.pathname.includes(subItem.navigate)
                          ? "active"
                          : ""
                      }${hasNoPermission ? " greyed-out" : ""}`}
                      onClick={(e) => {
                        if (hasNoPermission) {
                          e.preventDefault();
                          e.stopPropagation();
                          return;
                        }
                        navigate(
                          subItem.navigate.startsWith("/")
                            ? subItem.navigate
                            : `/${subItem.navigate}`,
                        );
                        if (isMobileOnly) {
                          dispatch(setIsMobileMenuOpen(false));
                        }
                      }}
                      style={{
                        cursor: hasNoPermission ? "not-allowed" : "pointer",
                        paddingLeft: "32px", // Indent sub-items
                        borderTop: "none",
                        backgroundColor: isMobileOnly ? "#192a4d" : undefined,
                      }}
                    >
                      <CustomerInquiryTextWrapper
                        style={{
                          display: "flex",
                          gap: "8px",
                          alignItems: "center",
                        }}
                        $disabled={hasNoPermission}
                      >
                        {subItem.icon && (
                          <span
                            style={{ display: "flex", alignItems: "center" }}
                          >
                            {cloneElement(subItem.icon, {
                              color: hasNoPermission ? "#ffffff" : "#333333",
                            })}
                          </span>
                        )}
                        {subItem.title}
                      </CustomerInquiryTextWrapper>
                    </div>
                  </SharedTooltip>
                );
              },
            )}
          </div>
        )}

        {!isMobileOnly && (
          <SharedTooltip
            title={
              isUserResigned ? (
                TOOLTIP_MESSAGES.USER_RETIRED
              ) : (
                <span style={{ textAlign: "center", display: "block" }}>
                  不明点はカスタマー
                  <br />
                  問合せに連絡
                </span>
              )
            }
          >
            <div
              className={`customer-inquiry-button last${
                isCollapse ? "collapse" : ""
              } ${
                location.pathname.includes(ROUTES.LIST_MESS) && !isUserResigned
                  ? "active"
                  : ""
              }`}
              onClick={() =>
                !isUserResigned && navigate(`/${ROUTES.LIST_MESS}`)
              }
            >
              <CustomerInquiryIconWrapper isCollapse={isCollapse}>
                <ChatIcon color={isUserResigned ? "#ffffff" : "#333333"} />
              </CustomerInquiryIconWrapper>
              {!isCollapse && (
                <CustomerInquiryTextWrapper $disabled={isUserResigned}>
                  カスタマー問合せ
                </CustomerInquiryTextWrapper>
              )}
            </div>
          </SharedTooltip>
        )}

        <div
          className={`wrap-footer ${isMobileOnly ? "mobile-footer" : ""} ${
            location.pathname.includes(ROUTES.LIST_MESS) ? "active" : ""
          }`}
        >
          {isMobileOnly ? (
            <Tooltip
              title={
                <span style={{ textAlign: "center", display: "block" }}>
                  不明点はカスタマー
                  <br />
                  問合せに連絡
                </span>
              }
              placement="top"
            >
              <span
                style={{ cursor: "pointer" }}
                onClick={() => {
                  if (isMobileOnly) {
                    dispatch(setIsMobileMenuOpen(false));
                  }
                  navigate(`/${ROUTES.LIST_MESS}`);
                }}
              >
                カスタマー問い合わせ
              </span>
            </Tooltip>
          ) : (
            <span>{RELEASE_VERSION}</span>
          )}
        </div>
      </div>
    </Styles>
  );
};

export default memo(SideBar);
